import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { UserResponse } from '@/api/responses'
import { logout } from '@/features/auth/api'
import { disconnectPresence } from '@/features/presence/api'
import { sessionQueryKey } from '@/features/auth/session'
import { tenantsQueryKey } from '@/features/tenants/queries'

import { useDisclosure } from './useDisclosure'

export interface UserMenuProps {
  readonly user: UserResponse
  /** Tenant atual: ao sair, o atendente deixa de contar como online nele na hora. */
  readonly tenantId?: string
}

function initialsOf(user: UserResponse): string {
  const initials = user.displayName
    .split(' ')
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

  return initials.length > 0 ? initials : user.email.charAt(0).toUpperCase()
}

/** Menu do usuario (perfil + logout), separado do tenant switcher. */
export function UserMenu({ user, tenantId }: UserMenuProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isOpen, setIsOpen, containerRef } = useDisclosure()

  const mutation = useMutation({
    mutationFn: async () => {
      if (tenantId !== undefined) {
        // Best-effort: se falhar, o heartbeat expira sozinho em poucos minutos.
        await disconnectPresence(tenantId).catch(() => undefined)
      }

      await logout()
    },
    onSuccess: () => {
      queryClient.setQueryData(sessionQueryKey, null)
      queryClient.removeQueries({ queryKey: tenantsQueryKey })
      void navigate({ to: '/login' })
    },
  })

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('auth.userMenu.openLabel')}
        onClick={() => {
          setIsOpen((value) => !value)
        }}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-action-primary text-sm font-semibold text-foreground-on-action focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
      >
        {initialsOf(user)}
      </button>
      {isOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 w-64 rounded-md border border-border-default bg-background-elevated py-1 shadow-sm"
        >
          <div className="px-3 py-2">
            <p className="text-xs text-foreground-muted">{t('auth.userMenu.signedInAs')}</p>
            <p className="truncate text-sm font-medium text-foreground-primary">{user.email}</p>
          </div>
          <div className="my-1 border-t border-border-default" />
          <button
            type="button"
            role="menuitem"
            disabled={mutation.isPending}
            onClick={() => {
              mutation.mutate()
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground-primary hover:bg-background-subtle disabled:opacity-60"
          >
            <LogOut aria-hidden="true" className="h-4 w-4" />
            {mutation.isPending ? t('auth.userMenu.loggingOut') : t('auth.userMenu.logout')}
          </button>
        </div>
      ) : null}
    </div>
  )
}
